import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { uploadUnitRealVideo } from '../utils/uploadUnitRealVideo';
import OptionalVideoUpload from './common/OptionalVideoUpload';
import {
  IMEI_QC_KEYS,
  OLD_MOBILE_GRADE_OPTIONS,
  buildDefaultUnitQcReport
} from '../constants/oldMobileQc';

const AdvancedEditModal = ({ show, product, onClose, onSave, loading }) => {
  const [editData, setEditData] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);
  const [savingUnitIdx, setSavingUnitIdx] = useState(null);
  const [unitPhotoUploadIdx, setUnitPhotoUploadIdx] = useState(null);
  const [unitVideoUploadIdx, setUnitVideoUploadIdx] = useState(null);
  const [pendingUnitVideoFile, setPendingUnitVideoFile] = useState(null);

  useEffect(() => {
    if (product) {
      console.log('📦 Modal opened - Product data loaded:', {
        id: product._id,
        name: product.name,
        category: product.category,
        specs: product.specs,
        qcReport: product.specs?.qcReport
      });
      
      // Initialize qcReport for Old Mobile products if not present
      let specs = product.specs || {};
      const isOldMobile = product.category === 'Old Mobile' || product.category?.toLowerCase() === 'old mobile';
      
      if (isOldMobile) {
        console.log('📱 Old Mobile detected - QC Report status:', {
          hasQcReport: !!specs.qcReport,
          qcReportType: typeof specs.qcReport,
          qcReportKeys: specs.qcReport ? Object.keys(specs.qcReport) : [],
          qcReportValue: specs.qcReport
        });
        
        if (!specs.qcReport || typeof specs.qcReport !== 'object' || Object.keys(specs.qcReport).length === 0) {
          console.log('⚠️ QC Report missing or empty - Initializing with defaults');
          // Initialize with default values (all working)
          specs = {
            ...specs,
            qcReport: buildDefaultUnitQcReport()
          };
        } else {
          const merged = { ...buildDefaultUnitQcReport(), ...specs.qcReport };
          if (specs.qcReport.backPanel === undefined && specs.qcReport.bodyPanel !== undefined) {
            merged.backPanel = specs.qcReport.bodyPanel;
          }
          specs = { ...specs, qcReport: merged };
        }
      }
      
      setEditData({
        ...product,
        specs: specs,
        tags: product.tags || [],
        features: product.features || [],
        oldMobileRealPhotos: product.oldMobileRealPhotos || [],
        acquiredFrom: product.acquiredFrom || {
          sellerName: '',
          sellerPhone: '',
          sellerAltPhone: '',
          sellerAddress: '',
          sellerNotes: '',
          acquisitionDate: ''
        }
      });
      
      console.log('✅ EditData set with specs:', {
        category: product.category,
        qcReport: specs.qcReport
      });
    }
  }, [product]);

  const updateSpecs = (field, value) => {
    const updatedSpecs = { ...editData.specs, [field]: value };
    console.log(`📝 Updating specs.${field}:`, value);
    console.log('📝 Updated specs object:', updatedSpecs);
    setEditData({
      ...editData,
      specs: updatedSpecs
    });
  };

  const updateTags = (value) => {
    // Split by comma, semicolon, newlines, pipes, or 2+ spaces. Keep single-space phrases intact
    const separators = /[\n,;|]+|\s{2,}/g;
    const normalized = value.replace(/\u2022|\u00B7|\u2219/g, ' '); // remove common bullet chars
    const parts = normalized
      .split(separators)
      .map(t => t.trim())
      .filter(Boolean);
    setEditData({ ...editData, tags: parts });
  };

  const updateFeatures = (value) => {
    // Allow comma, semicolon, newline, bullet separators
    const normalized = value
      .replace(/\u2022|\u00B7|\u2219/g, '\n') // bullets to newline
      .replace(/,|;|\|/g, '\n');
    const features = normalized
      .split(/\n+/)
      .map(f => f.trim())
      .filter(Boolean);
    setEditData({ ...editData, features });
  };

  // Helper function for QC Report checkbox handling
  const handleQCCheck = (field, checked) => {
    const qcReport = editData.specs?.qcReport || {};
    if (checked) {
      qcReport[field] = true;
    } else {
      qcReport[field] = 'issue';
    }
    console.log(`QC Report updated for ${field}:`, qcReport);
    updateSpecs('qcReport', qcReport);
  };

  // Helper function for QC Report issue text input
  const handleQCIssueText = (field, value) => {
    const qcReport = editData.specs?.qcReport || {};
    qcReport[field] = value || 'issue';
    updateSpecs('qcReport', qcReport);
  };

  // Tick All QC Report items
  const handleTickAllQC = () => {
    const qcReport = buildDefaultUnitQcReport();
    console.log('✓ Tick All QC Report clicked:', qcReport);
    updateSpecs('qcReport', qcReport);
  };

  const uploadOldMobileRealPhotos = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const existing = editData.oldMobileRealPhotos || [];
    const room = 3 - existing.length;
    if (room <= 0) {
      alert('Maximum 3 real photos. Remove one first.');
      e.target.value = '';
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      let added = 0;
      for (let i = 0; i < files.length && added < room; i++) {
        fd.append('photos', files[i]);
        added++;
      }
      const res = await axios.post(
        `${config.API_URL}/products/${editData._id}/old-mobile-real-photos`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setEditData({ ...editData, oldMobileRealPhotos: res.data.oldMobileRealPhotos || [] });
      e.target.value = '';
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removeOldMobilePhotoAt = (idx) => {
    const list = [...(editData.oldMobileRealPhotos || [])];
    list.splice(idx, 1);
    setEditData({ ...editData, oldMobileRealPhotos: list });
  };

  const patchImeiRow = (idx, updater) => {
    setEditData((prev) => {
      const list = [...(prev.imeiTracking || [])];
      if (!list[idx]) return prev;
      const row = { ...list[idx] };
      updater(row);
      list[idx] = row;
      return { ...prev, imeiTracking: list };
    });
  };

  const saveImeiUnit = async (idx) => {
    const row = (editData.imeiTracking || [])[idx];
    if (!row || !editData._id) return;
    setSavingUnitIdx(idx);
    try {
      const isMobileOnly = String(editData.category || '').trim() === 'Mobile';
      if (isMobileOnly) {
        await axios.patch(`${config.API_URL}/products/${editData._id}/imei/${idx}/unit-details`, {
          transferListPrice:
            row.transferListPrice === '' || row.transferListPrice == null
              ? null
              : Number(row.transferListPrice),
          transferOfferPrice:
            row.transferOfferPrice === '' || row.transferOfferPrice == null
              ? null
              : Number(row.transferOfferPrice)
        });
      } else {
        const payload = {
          unitSeller: {
            sellerName: row.unitSeller?.sellerName || '',
            sellerPhone: row.unitSeller?.sellerPhone || ''
          },
          unitQcReport: row.unitQcReport && typeof row.unitQcReport === 'object' ? row.unitQcReport : {},
          unitConditionGrade: row.unitConditionGrade || ''
        };
        if (row.transferIn?.fromBranchId) {
          payload.transferListPrice =
            row.transferListPrice === '' || row.transferListPrice == null
              ? null
              : Number(row.transferListPrice);
          payload.transferOfferPrice =
            row.transferOfferPrice === '' || row.transferOfferPrice == null
              ? null
              : Number(row.transferOfferPrice);
        }
        await axios.patch(`${config.API_URL}/products/${editData._id}/imei/${idx}/unit-details`, payload);
      }
      alert('Is IMEI unit ka record save ho gaya.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSavingUnitIdx(null);
    }
  };

  const uploadImeiUnitPhotos = async (e, idx) => {
    const files = e.target.files;
    if (!files?.length || !editData._id) return;
    setUnitPhotoUploadIdx(idx);
    try {
      const fd = new FormData();
      for (let i = 0; i < files.length && i < 3; i++) {
        fd.append('photos', files[i]);
      }
      const res = await axios.post(
        `${config.API_URL}/products/${editData._id}/imei/${idx}/unit-real-photos`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const urls = res.data.unitRealPhotos || [];
      patchImeiRow(idx, (row) => {
        row.unitRealPhotos = urls;
      });
      e.target.value = '';
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUnitPhotoUploadIdx(null);
    }
  };

  const uploadImeiUnitVideo = async (idx) => {
    if (!pendingUnitVideoFile || !editData._id) return;
    setUnitVideoUploadIdx(idx);
    try {
      const res = await uploadUnitRealVideo(pendingUnitVideoFile, {
        productId: editData._id,
        imeiIndex: idx
      });
      if (!res.success) throw new Error(res.error || 'Upload failed');
      patchImeiRow(idx, (row) => {
        row.unitRealVideoUrl = res.videoUrl || '';
        row.unitRealVideoStatus = res.processing ? 'processing' : 'ready';
        row.unitRealVideoError = '';
      });
      setPendingUnitVideoFile(null);
    } catch (err) {
      alert(err.message || 'Video upload failed');
    } finally {
      setUnitVideoUploadIdx(null);
    }
  };

  const removeImeiUnitVideo = async (idx) => {
    setSavingUnitIdx(idx);
    try {
      await axios.patch(`${config.API_URL}/products/${editData._id}/imei/${idx}/unit-details`, {
        unitRealVideoUrl: ''
      });
      patchImeiRow(idx, (r) => {
        r.unitRealVideoUrl = '';
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Remove failed');
    } finally {
      setSavingUnitIdx(null);
    }
  };

  const removeImeiUnitPhoto = async (idx, photoIdx) => {
    const row = (editData.imeiTracking || [])[idx];
    const urls = [...(row.unitRealPhotos || [])];
    urls.splice(photoIdx, 1);
    setSavingUnitIdx(idx);
    try {
      await axios.patch(`${config.API_URL}/products/${editData._id}/imei/${idx}/unit-details`, {
        unitRealPhotos: urls
      });
      patchImeiRow(idx, (r) => {
        r.unitRealPhotos = urls;
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Remove failed');
    } finally {
      setSavingUnitIdx(null);
    }
  };

  const handleImeiQCCheck = (idx, key, checked) => {
    patchImeiRow(idx, (row) => {
      const qr = { ...(row.unitQcReport || {}) };
      qr[key] = checked ? true : 'issue';
      row.unitQcReport = qr;
    });
  };

  const handleImeiQCIssueText = (idx, key, value) => {
    patchImeiRow(idx, (row) => {
      const qr = { ...(row.unitQcReport || {}) };
      qr[key] = value || 'issue';
      row.unitQcReport = qr;
    });
  };

  if (!show || !product) return null;

  const isOldMobileCat =
    editData.category === 'Old Mobile' || editData.category?.toLowerCase() === 'old mobile';

  const isMobileCat = String(editData.category || '').trim() === 'Mobile';

  const omHasUnits =
    (isOldMobileCat || isMobileCat) &&
    Array.isArray(editData.imeiTracking) &&
    editData.imeiTracking.length > 0;

  const mobileImeiCard =
    Array.isArray(editData.imeiTracking) &&
    editData.imeiTracking.length > 0 &&
    (isOldMobileCat || isMobileCat);

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={`modal-dialog ${isOldMobileCat ? 'modal-fullscreen-sm-down' : ''} modal-xl`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {product.category === 'Old Mobile' ? '📱 Properly Edit (Quality & Condition):' : '🛠️ Advanced Edit:'} {product.name}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div
            className="modal-body pb-5"
            style={{ maxHeight: isOldMobileCat ? 'calc(100dvh - 130px)' : '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
          >
            <div className="row">
              {/* Basic Information */}
              <div className="col-md-6">
                <h6 className="text-primary mb-3">📝 Basic Information</h6>
                
                <div className="mb-3">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={editData.description || ''}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Category *</label>
                  <select
                    className="form-select"
                    value={editData.category || ''}
                    onChange={(e) => setEditData({...editData, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    <option value="Mobile">📱 Mobile</option>
                    <option value="Old Mobile">📲 Old Mobile</option>
                    <option value="Speaker">🔊 Speaker</option>
                    <option value="Home Theatre">🎬 Home Theatre</option>
                    <option value="Soundbar">📢 Soundbar</option>
                    <option value="Neckband">🎧 Neckband</option>
                    <option value="Airpods">🎵 Airpods</option>
                    <option value="Smartwatches">⌚ Smartwatches</option>
                    <option value="TV">📺 TV</option>
                    <option value="Refrigerator">❄️ Refrigerator</option>
                    <option value="Washing Machine">🧺 Washing Machine</option>
                    <option value="AC">❄️ AC</option>
                    <option value="FAN">💨 FAN</option>
                    <option value="Microwave">🔥 Microwave</option>
                    <option value="Mixer/Juicer">🥤 Mixer/Juicer</option>
                    <option value="Other Accessories">🔧 Other Accessories</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Brand</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.brand || ''}
                    onChange={(e) => setEditData({...editData, brand: e.target.value})}
                    placeholder="e.g., Samsung, Apple, OnePlus"
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Buying Price (₹) *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editData.buyingPrice || ''}
                      onChange={(e) => setEditData({...editData, buyingPrice: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Selling Price (₹) *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editData.price || ''}
                      onChange={(e) => setEditData({...editData, price: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Offer Price (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editData.offerPrice || ''}
                      onChange={(e) => setEditData({...editData, offerPrice: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Stock *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editData.stock || ''}
                      onChange={(e) => setEditData({...editData, stock: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Technical Specifications */}
              <div className="col-md-6">
                <h6 className="text-success mb-3">⚙️ Technical Specifications</h6>
                
                {/* Mobile/Electronics Specs */}
                {(editData.category === 'Mobile' || editData.category === 'Smartwatches') && (
                  <>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">RAM (GB)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editData.specs?.ramGB || ''}
                          onChange={(e) => updateSpecs('ramGB', e.target.value)}
                          placeholder="e.g., 8"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Storage (GB)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editData.specs?.storageGB || ''}
                          onChange={(e) => updateSpecs('storageGB', e.target.value)}
                          placeholder="e.g., 128"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Rear Camera (MP)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editData.specs?.rearCameraMP || ''}
                          onChange={(e) => updateSpecs('rearCameraMP', e.target.value)}
                          placeholder="e.g., 48"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Front Camera (MP)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editData.specs?.frontCameraMP || ''}
                          onChange={(e) => updateSpecs('frontCameraMP', e.target.value)}
                          placeholder="e.g., 16"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Processor</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.specs?.processor || ''}
                        onChange={(e) => updateSpecs('processor', e.target.value)}
                        placeholder="e.g., Snapdragon 778G"
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Battery (mAh)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editData.specs?.batteryMah || ''}
                          onChange={(e) => updateSpecs('batteryMah', e.target.value)}
                          placeholder="e.g., 5000"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Display (inches)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-control"
                          value={editData.specs?.displayInches || ''}
                          onChange={(e) => updateSpecs('displayInches', e.target.value)}
                          placeholder="e.g., 6.4"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Network</label>
                        <select
                          className="form-select"
                          value={editData.specs?.network || ''}
                          onChange={(e) => updateSpecs('network', e.target.value)}
                        >
                          <option value="">Select Network</option>
                          <option value="4G">4G</option>
                          <option value="5G">5G</option>
                          <option value="WiFi Only">WiFi Only</option>
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Operating System</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editData.specs?.os || ''}
                          onChange={(e) => updateSpecs('os', e.target.value)}
                          placeholder="e.g., Android 13"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Color</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.specs?.color || ''}
                        onChange={(e) => updateSpecs('color', e.target.value)}
                        placeholder="e.g., Midnight Black"
                      />
                    </div>
                  </>
                )}

                {/* Old Mobile Quality & Condition Details */}
                {(editData.category === 'Old Mobile' || editData.category?.toLowerCase() === 'old mobile') && (
                  <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="card-header bg-warning text-dark">
                      <h6 className="mb-0">📱 Old Mobile Quality & Condition Details</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Condition Grade *</label>
                          <select
                            className="form-select"
                            value={editData.specs?.conditionGrade || ''}
                            onChange={(e) => updateSpecs('conditionGrade', e.target.value)}
                          >
                            <option value="">Select Grade</option>
                            {OLD_MOBILE_GRADE_OPTIONS.map((g) => (
                              <option key={g.value} value={g.value}>
                                {g.label}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">Select the overall condition grade</small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Battery Health (%)</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            max="100"
                            value={editData.specs?.batteryHealth || ''}
                            onChange={(e) => updateSpecs('batteryHealth', e.target.value)}
                            placeholder="e.g., 85"
                          />
                          <small className="text-muted">Battery health percentage (0-100%)</small>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Screen Condition</label>
                          <select
                            className="form-select"
                            value={editData.specs?.screenCondition || ''}
                            onChange={(e) => updateSpecs('screenCondition', e.target.value)}
                          >
                            <option value="">Select Condition</option>
                            <option value="Perfect">Perfect - No scratches</option>
                            <option value="Minor Scratches">Minor Scratches - Barely visible</option>
                            <option value="Visible Scratches">Visible Scratches - Noticeable</option>
                            <option value="Cracked">Cracked - Screen damaged</option>
                            <option value="Replaced">Replaced - Screen replaced</option>
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Body Scratches Level</label>
                          <select
                            className="form-select"
                            value={editData.specs?.scratchesLevel || ''}
                            onChange={(e) => updateSpecs('scratchesLevel', e.target.value)}
                          >
                            <option value="">Select Level</option>
                            <option value="None">None - No scratches</option>
                            <option value="Minor">Minor - Small scratches</option>
                            <option value="Moderate">Moderate - Visible scratches</option>
                            <option value="Heavy">Heavy - Many scratches</option>
                            <option value="Dents">Dents - Physical damage</option>
                          </select>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Age (Months)</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={editData.specs?.ageMonths || ''}
                            onChange={(e) => updateSpecs('ageMonths', e.target.value)}
                            placeholder="e.g., 12"
                          />
                          <small className="text-muted">How old is the device in months?</small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Purchase Year</label>
                          <input
                            type="number"
                            className="form-control"
                            min="2010"
                            max={new Date().getFullYear()}
                            value={editData.specs?.purchaseYear || ''}
                            onChange={(e) => updateSpecs('purchaseYear', e.target.value)}
                            placeholder={`e.g., ${new Date().getFullYear() - 1}`}
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className={omHasUnits ? 'col-md-12 mb-3' : 'col-md-6 mb-3'}>
                          <label className="form-label">Warranty (Months)</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={editData.specs?.warrantyMonths || ''}
                            onChange={(e) => updateSpecs('warrantyMonths', e.target.value)}
                            placeholder="e.g., 3"
                          />
                          <small className="text-muted">Remaining warranty period</small>
                        </div>
                        {!omHasUnits ? (
                          <div className="col-md-6 mb-3">
                            <label className="form-label">IMEI 1</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editData.specs?.imei1 || ''}
                              onChange={(e) => updateSpecs('imei1', e.target.value)}
                              placeholder="15-digit IMEI"
                              maxLength="15"
                            />
                          </div>
                        ) : (
                          <div className="col-12 mb-2">
                            <div className="alert alert-light border small py-2 mb-0">
                              <strong>IMEI stock</strong> ab alag-alag units me hai — yahan specs wala IMEI field use mat karein; neeche{' '}
                              <strong>Har IMEI ka apna QC / photos</strong> section dekhein ya Inventory → IMEI Management.
                            </div>
                          </div>
                        )}
                      </div>

                      {!omHasUnits && (
                        <div className="mb-3">
                          <label className="form-label">IMEI 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editData.specs?.imei2 || ''}
                            onChange={(e) => updateSpecs('imei2', e.target.value)}
                            placeholder="15-digit IMEI (if dual SIM)"
                            maxLength="15"
                          />
                        </div>
                      )}

                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <label className="form-label">Accessories Included</label>
                          <div className="row">
                            <div className="col-md-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={editData.specs?.hasBox || false}
                                  onChange={(e) => updateSpecs('hasBox', e.target.checked)}
                                />
                                <label className="form-check-label">Original Box</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={editData.specs?.hasBill || false}
                                  onChange={(e) => updateSpecs('hasBill', e.target.checked)}
                                />
                                <label className="form-check-label">Bill/Invoice</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={editData.specs?.hasCharger || false}
                                  onChange={(e) => updateSpecs('hasCharger', e.target.checked)}
                                />
                                <label className="form-check-label">Charger</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={editData.specs?.hasEarphones || false}
                                  onChange={(e) => updateSpecs('hasEarphones', e.target.checked)}
                                />
                                <label className="form-check-label">Earphones</label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editData.specs?.refurbished || false}
                            onChange={(e) => updateSpecs('refurbished', e.target.checked)}
                          />
                          <label className="form-check-label">
                            <strong>Refurbished</strong> - Device has been repaired/refurbished
                          </label>
                        </div>
                      </div>

                      {/* Quality Summary Display */}
                      {(editData.specs?.conditionGrade || editData.specs?.batteryHealth) && (
                        <div className="alert alert-info mb-0 mt-3">
                          <strong>Quality Summary:</strong>
                          {editData.specs?.conditionGrade && (
                            <span className="badge bg-primary ms-2">Grade: {editData.specs.conditionGrade}</span>
                          )}
                          {editData.specs?.batteryHealth && (
                            <span className="badge bg-success ms-2">Battery: {editData.specs.batteryHealth}%</span>
                          )}
                          {editData.specs?.screenCondition && (
                            <span className="badge bg-info ms-2">Screen: {editData.specs.screenCondition}</span>
                          )}
                          {editData.specs?.refurbished && (
                            <span className="badge bg-warning text-dark ms-2">Refurbished</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {mobileImeiCard && (
                  <div className="card mb-3 border-primary">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">
                        {isOldMobileCat
                          ? '📱 Har IMEI alag — QC, asli photos, seller (zaroori)'
                          : '📱 Har IMEI — transfer-in par branch price'}
                      </h6>
                    </div>
                    <div className="card-body">
                      <p className="text-muted small mb-3">
                        {isOldMobileCat ? (
                          <>
                            3 unit = 3 alag physical phones — inka <strong>QC / real pics / seller</strong> yahin per-IMEI save hota hai.
                            Upar wala &quot;listing&quot; QC / gallery tabhi use karein jab <strong>koi IMEI row na ho</strong> (purana data).
                          </>
                        ) : (
                          <>
                            Jin units ko <strong>stock transfer</strong> se mila hai, unka branch par alag selling / offer price set kar sakte ho.
                            Khali chhodne par <strong>product listing</strong> wali price (₹{editData.price ?? '—'}) lagti hai. Punjab / main branch wapas transfer par wahi listing price active rehti hai.
                          </>
                        )}
                      </p>
                      {(editData.imeiTracking || []).map((row, idx) => (
                        <details key={`imei-unit-${idx}-${row.imei1}`} className="border rounded mb-2 p-2" open={idx === 0}>
                          <summary className="fw-semibold cursor-pointer">
                            Unit #{idx + 1} · <code>{row.imei1}</code>{' '}
                            {row.transferIn?.fromBranchId ? (
                              <span className="badge bg-info text-dark ms-1">
                                Transfer ← {row.transferIn.fromBranchId}
                              </span>
                            ) : null}
                            {isOldMobileCat && row.unitConditionGrade ? (
                              <span className="badge bg-primary ms-1">Grade {row.unitConditionGrade}</span>
                            ) : null}
                            <span className="badge bg-secondary ms-1">{row.status || '?'}</span>
                          </summary>
                          <div className="pt-3 mt-2 border-top">
                            {row.transferIn?.fromBranchId ? (
                              <div className="border rounded p-2 mb-3 bg-light">
                                <div className="small fw-semibold text-primary mb-2">
                                  Branch price (sirf is transfer unit ke liye)
                                </div>
                                <div className="row">
                                  <div className="col-md-4 mb-2">
                                    <label className="form-label small">Selling ₹ (branch)</label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      className="form-control form-control-sm"
                                      placeholder={`Listing: ₹${editData.price ?? ''}`}
                                      value={
                                        row.transferListPrice === undefined || row.transferListPrice === null
                                          ? ''
                                          : row.transferListPrice
                                      }
                                      onChange={(e) =>
                                        patchImeiRow(idx, (r) => {
                                          r.transferListPrice = e.target.value === '' ? '' : e.target.value;
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-md-4 mb-2">
                                    <label className="form-label small">Offer ₹ (branch, optional)</label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      className="form-control form-control-sm"
                                      placeholder="Khali = listing offer"
                                      value={
                                        row.transferOfferPrice === undefined || row.transferOfferPrice === null
                                          ? ''
                                          : row.transferOfferPrice
                                      }
                                      onChange={(e) =>
                                        patchImeiRow(idx, (r) => {
                                          r.transferOfferPrice = e.target.value === '' ? '' : e.target.value;
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                                <small className="text-muted d-block">
                                  Dono khali = product ki default selling / offer price use hogi.
                                </small>
                              </div>
                            ) : isMobileCat ? (
                              <p className="text-muted small mb-2">
                                Yeh unit transfer-in nahi hai — price <strong>Edit product / listing</strong> se badlein.
                              </p>
                            ) : null}

                            {isOldMobileCat && (
                              <>
                                <div className="row mb-2">
                                  <div className="col-md-4 mb-2">
                                    <label className="form-label small">Condition grade *</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={row.unitConditionGrade || ''}
                                      onChange={(e) =>
                                        patchImeiRow(idx, (r) => {
                                          r.unitConditionGrade = e.target.value;
                                        })
                                      }
                                    >
                                      <option value="">Select grade</option>
                                      {OLD_MOBILE_GRADE_OPTIONS.map((g) => (
                                        <option key={g.value} value={g.value}>
                                          {g.value}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="col-md-4 mb-2">
                                    <label className="form-label small">Seller name (is unit)</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={row.unitSeller?.sellerName || ''}
                                      onChange={(e) =>
                                        patchImeiRow(idx, (r) => {
                                          r.unitSeller = { ...r.unitSeller, sellerName: e.target.value };
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-md-4 mb-2">
                                    <label className="form-label small">Seller phone</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={row.unitSeller?.sellerPhone || ''}
                                      onChange={(e) =>
                                        patchImeiRow(idx, (r) => {
                                          r.unitSeller = { ...r.unitSeller, sellerPhone: e.target.value };
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <span className="small fw-semibold d-block mb-1">Asli photos (max 3)</span>
                                  {row.unitRealVideoUrl ? (
                                    <small className="text-warning d-block mb-1">
                                      Is unit par video hai — pehle video hatao, phir photos add karein.
                                    </small>
                                  ) : null}
                                  <div className="d-flex flex-wrap gap-2 mb-2">
                                    {(row.unitRealPhotos || []).map((url, pIdx) => (
                                      <div key={url} className="position-relative" style={{ width: 88, height: 88 }}>
                                        <img
                                          src={url}
                                          alt=""
                                          className="rounded border"
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger position-absolute top-0 end-0 py-0 px-1"
                                          onClick={() => removeImeiUnitPhoto(idx, pIdx)}
                                          disabled={savingUnitIdx === idx || !!row.unitRealVideoUrl}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <input
                                    type="file"
                                    className="form-control form-control-sm"
                                    accept="image/jpeg,image/png,image/webp"
                                    multiple
                                    disabled={
                                      !!row.unitRealVideoUrl ||
                                      unitPhotoUploadIdx === idx ||
                                      (row.unitRealPhotos || []).length >= 3
                                    }
                                    onChange={(e) => uploadImeiUnitPhotos(e, idx)}
                                  />
                                  {unitPhotoUploadIdx === idx && (
                                    <small className="text-muted">Upload…</small>
                                  )}
                                </div>
                                <div className="mb-2">
                                  <span className="small fw-semibold d-block mb-1">Condition video (~30s, ≤5MB)</span>
                                  {(row.unitRealPhotos || []).length > 0 ? (
                                    <small className="text-warning d-block mb-1">
                                      Photos hain — video ke liye pehle photos hata dein.
                                    </small>
                                  ) : null}
                                  {row.unitRealVideoUrl ? (
                                    <div className="mb-2">
                                      <video
                                        src={row.unitRealVideoUrl}
                                        controls
                                        playsInline
                                        style={{ width: '100%', maxHeight: 140, borderRadius: 8 }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger mt-1"
                                        onClick={() => removeImeiUnitVideo(idx)}
                                        disabled={savingUnitIdx === idx}
                                      >
                                        Remove video
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <OptionalVideoUpload
                                        title=""
                                        file={unitVideoUploadIdx === idx ? pendingUnitVideoFile : null}
                                        onChange={(f) => {
                                          setUnitVideoUploadIdx(idx);
                                          setPendingUnitVideoFile(f);
                                        }}
                                        className="mb-1"
                                      />
                                      {pendingUnitVideoFile && unitVideoUploadIdx === idx ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          disabled={!!row.unitRealPhotos?.length}
                                          onClick={() => uploadImeiUnitVideo(idx)}
                                        >
                                          Upload video
                                        </button>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                                <div className="mb-2">
                                  <span className="small fw-semibold">QC (is phone)</span>
                                  <div className="row g-1 mt-1">
                                    {IMEI_QC_KEYS.map(({ key, label }) => {
                                      const qr = row.unitQcReport || {};
                                      const v = qr[key];
                                      const isWorking = v === true || v === 'working' || v === undefined;
                                      return (
                                        <div key={key} className="col-lg-4 col-md-6">
                                          <label className="form-check-label small d-flex align-items-start gap-1">
                                            <input
                                              type="checkbox"
                                              className="form-check-input mt-1"
                                              checked={isWorking}
                                              onChange={(e) => handleImeiQCCheck(idx, key, e.target.checked)}
                                            />
                                            <span>{label}</span>
                                          </label>
                                          {!isWorking && (
                                            <input
                                              type="text"
                                              className="form-control form-control-sm mt-1"
                                              placeholder="Issue detail"
                                              value={typeof v === 'string' && v !== 'issue' ? v : ''}
                                              onChange={(e) => handleImeiQCIssueText(idx, key, e.target.value)}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="d-grid d-md-inline-block">
                              <button
                                type="button"
                                className="btn btn-success py-2 px-4"
                                style={{ minWidth: 160 }}
                                onClick={() => saveImeiUnit(idx)}
                                disabled={savingUnitIdx === idx}
                              >
                                {savingUnitIdx === idx ? 'Saving…' : 'Save is unit'}
                              </button>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}

                {/* Real product photos — Old Mobile only (customer site slider) — single-SKU / legacy */}
                {!omHasUnits && (editData.category === 'Old Mobile' || editData.category?.toLowerCase() === 'old mobile') && (
                  <div className="card mb-3 border-success">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">📷 Asli product photos (max 3)</h6>
                    </div>
                    <div className="card-body">
                      <p className="text-muted small mb-2">
                        Grade / listing image alag hai — yahan phone ki asli photos daaliye. <strong>1–3 files ek hi baar</strong> select kar sakte ho (Ctrl/Shift se multiple). Upload par auto WebP optimize. Customer site par slider mein dikhengi.
                      </p>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {(editData.oldMobileRealPhotos || []).map((url, idx) => (
                          <div key={idx} className="position-relative" style={{ width: 100, height: 100 }}>
                            <img
                              src={url}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid #dee2e6' }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 py-0 px-1"
                              title="Remove"
                              onClick={() => removeOldMobilePhotoAt(idx)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        type="file"
                        className="form-control form-control-sm"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        disabled={photoUploading || (editData.oldMobileRealPhotos || []).length >= 3}
                        onChange={uploadOldMobileRealPhotos}
                      />
                      {photoUploading && <small className="text-muted">Upload ho raha hai…</small>}
                      <div className="mt-1 small text-muted">
                        {(editData.oldMobileRealPhotos || []).length}/3 — Save dabane par hatai gayi files server se bhi saaf ho jayengi.
                      </div>
                    </div>
                  </div>
                )}

                {/* QC Report — listing / legacy (jab koi IMEI row nahi) */}
                {!omHasUnits && (editData.category === 'Old Mobile' || editData.category?.toLowerCase() === 'old mobile') && (
                  <div className="card mb-3" style={{ backgroundColor: '#e7f3ff' }}>
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">🔍 QC Report (Quality Control Check)</h6>
                    </div>
                    <div className="card-body">
                      <p className="text-muted small mb-3">
                        <i className="bi bi-info-circle me-1"></i>
                        Mark all functional checks. If any feature has issues, uncheck it and describe the problem.
                      </p>
                      
                      <div className="mb-3">
                        <button 
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={handleTickAllQC}
                        >
                          ✓ Tick All (All Working)
                        </button>
                      </div>

                      <div className="row">
                        {IMEI_QC_KEYS.map(({ key, label }) => {
                          const qcReport = editData.specs?.qcReport || {};
                          // Check if working: true, 'working', 'ok', or undefined (defaults to working)
                          const value = qcReport[key];
                          const isWorking = value === true || value === 'working' || value === 'ok' || value === undefined;
                          // Get issue text if it's a string and not a working status
                          const issueText = (typeof value === 'string' && value !== 'working' && value !== 'ok' && value !== 'issue' && value) ? value : '';
                          
                          return (
                            <div key={key} className="col-md-6 mb-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isWorking}
                                  onChange={(e) => {
                                    console.log(`QC Checkbox changed: ${key} = ${e.target.checked}`);
                                    handleQCCheck(key, e.target.checked);
                                  }}
                                />
                                <label className="form-check-label">
                                  <strong>{label}</strong>
                                </label>
                              </div>
                              {!isWorking && (
                                <input
                                  type="text"
                                  className="form-control mt-2"
                                  placeholder={`Describe ${label.toLowerCase()} issue...`}
                                  value={issueText}
                                  onChange={(e) => {
                                    console.log(`QC Issue text changed: ${key} = ${e.target.value}`);
                                    handleQCIssueText(key, e.target.value);
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Seller — listing / legacy (jab koi IMEI row nahi) */}
                {!omHasUnits && (editData.category === 'Old Mobile' || editData.category?.toLowerCase() === 'old mobile') && (
                  <div className="card mb-3" style={{ backgroundColor: '#fff3cd' }}>
                    <div className="card-header bg-warning text-dark">
                      <h6 className="mb-0">
                        <i className="bi bi-person-badge me-2"></i>
                        Seller Details (Optional - For Record Keeping)
                      </h6>
                    </div>
                    <div className="card-body">
                      <p className="text-muted small mb-3">
                        <i className="bi bi-info-circle me-1"></i>
                        Fill seller information if this mobile was purchased from an individual seller. This is optional but recommended for record-keeping purposes.
                      </p>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Seller Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter seller name"
                            value={editData.acquiredFrom?.sellerName || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                sellerName: e.target.value
                              }
                            })}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Seller Phone</label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter seller phone number"
                            value={editData.acquiredFrom?.sellerPhone || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                sellerPhone: e.target.value
                              }
                            })}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Alternate Phone (Optional)</label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter alternate phone"
                            value={editData.acquiredFrom?.sellerAltPhone || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                sellerAltPhone: e.target.value
                              }
                            })}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Purchase Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={editData.acquiredFrom?.acquisitionDate ? new Date(editData.acquiredFrom.acquisitionDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                acquisitionDate: e.target.value ? new Date(e.target.value) : null
                              }
                            })}
                          />
                        </div>
                        <div className="col-md-12 mb-3">
                          <label className="form-label">Seller Address (Optional)</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            placeholder="Enter seller address"
                            value={editData.acquiredFrom?.sellerAddress || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                sellerAddress: e.target.value
                              }
                            })}
                          />
                        </div>
                        <div className="col-md-12 mb-0">
                          <label className="form-label">Notes (Optional)</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            placeholder="Any additional notes about the seller or purchase"
                            value={editData.acquiredFrom?.sellerNotes || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              acquiredFrom: {
                                ...editData.acquiredFrom,
                                sellerNotes: e.target.value
                              }
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other category specs can be added here */}

                {!isOldMobileCat && (
                <div className="mb-3">
                  <label className="form-label">Tags (comma / semicolon / multi-space separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.tags?.join(', ') || ''}
                    onChange={(e) => updateTags(e.target.value)}
                    placeholder="e.g., flagship, gaming, camera"
                  />
                  {/* Live parsed preview */}
                  {Array.isArray(editData.tags) && editData.tags.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {editData.tags.map((tag, idx) => (
                        <span key={idx} className="badge bg-secondary">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {!isOldMobileCat && (
                <div className="mb-3">
                  <label className="form-label">Key Features (comma / semicolon / new line)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={editData.features?.join('\n') || ''}
                    onChange={(e) => updateFeatures(e.target.value)}
                    placeholder="Fast charging&#10;Water resistant"
                  />
                  {Array.isArray(editData.features) && editData.features.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">{editData.features.length} feature(s) parsed</small>
                      <ul className="mb-0">
                        {editData.features.slice(0,5).map((f, idx) => (
                          <li key={idx}>{f}</li>
                        ))}
                        {editData.features.length > 5 && (
                          <li>+ {editData.features.length - 5} more...</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Image URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.imageUrl || ''}
                    onChange={(e) => setEditData({...editData, imageUrl: e.target.value})}
                    placeholder="https://example.com/image.png"
                  />
                  {editData.imageUrl && (
                    <div className="mt-2">
                      <img 
                        src={editData.imageUrl} 
                        alt="Preview" 
                        className="img-thumbnail" 
                        style={{ maxHeight: '80px', maxWidth: '80px' }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={editData.emiAvailable || false}
                    onChange={(e) => setEditData({...editData, emiAvailable: e.target.checked})}
                  />
                  <label className="form-check-label">EMI Available</label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-success"
              onClick={() => {
                console.log('💾 Saving product with QC Report:', {
                  category: editData.category,
                  qcReport: editData.specs?.qcReport,
                  fullSpecs: editData.specs
                });
                onSave(editData);
              }}
              disabled={loading}
            >
              {loading ? 'Saving...' : '💾 Save All Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedEditModal;
